import { useState, useEffect } from "react";
import { eventDateGridFormatter } from "../../../../../utils/dates";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { MenuItem, Select, Button, RadioGroup, FormControlLabel, Radio, FormControl } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs'; 

/**
 * Custom cell component for editing sale dates in the grid
 * Allows selecting existing dates or adding new ones
 */
const EditSaleDateCell = ({ params }) => {
  const [isDateTimePickerOpen, setIsDateTimePickerOpen] = useState(false);
  const [localSaleType, setLocalSaleType] = useState(1);

  const defaultSaleTime = dayjs().hour(10).minute(0);

  // Generate options for the select dropdown
  const generateSalesDatesValueOptions = (params) => {
    const { salesDates = [], newSalesDates = [], salesDate } = params.row;
    const options = [];

    // Add existing sale dates
    if (salesDates?.length > 0) {
      const now = new Date();
      salesDates.forEach(saleDate => {
        const saleDateTime = new Date(saleDate.saleDate + 'Z');
        if (saleDateTime >= now || saleDate.saleDate === salesDate) {
          options.push({ 
            value: saleDate.saleDate, 
            label: eventDateGridFormatter(new Date(saleDate.saleDate + 'Z'))
          });
        }
      });
    }

    if(!salesDates.some(s => s.saleDate === salesDate) && salesDate !== "now" && salesDate !== "IsMonitor" && salesDate !== "IsTraining") {
      options.push({ 
        value: salesDate, 
        label: eventDateGridFormatter(new Date(salesDate + 'Z'))
      });
    }

    // Add new sale dates
    if (newSalesDates?.length > 0) {
      newSalesDates.forEach(newSaleDate => {
        options.push({ 
          value: newSaleDate.saleDate,
          label: `${eventDateGridFormatter(new Date(newSaleDate.saleDate))}`
        });
      });
    }
    
    // Add special options
    options.push(
      { value: "now", label: "NOW" },
      { value: "IsMonitor", label: "Monitor for Drops" },
      { value: "IsTraining", label: "TRAINING" },
      { value: "newSaleDate", label: "New Sale Date" }
    );
    
    return options;
  };

  // Handlers
  const handleSelectChange = (event) => {
    const value = event.target.value;
    if (value === "newSaleDate") {
      setIsDateTimePickerOpen(true);
    } else {
      params.api.setEditCellValue({ 
        id: params.id, 
        field: params.field, 
        value
      }, true);
    }
  };

  const handleDateChange = (newDate) => {
    if (!newDate) return;

    const saleType = params.saleType || 1; // PreSale type
    const newSaleDateObj = {
      saleType,
      saleDate: newDate.toISOString()
    };

    // Update newSalesDates array
    const currentNewSalesDates = [newSaleDateObj];

    // Actualizar ambos valores en una sola operación
    Promise.all([
      params.api.setEditCellValue({ 
        id: params.id, 
        field: 'newSalesDates', 
        value: currentNewSalesDates
      }, true),
      params.api.setEditCellValue({ 
        id: params.id, 
        field: params.field, 
        value: newSaleDateObj.saleDate
      }, true)
    ]).then(() => {
      // Forzar actualización del row
      params.api.getRow(params.id);
    });

    setIsDateTimePickerOpen(false);
  };

  return (
    <div style={{ width: "100%" }}>           
      {isDateTimePickerOpen ? (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            value={defaultSaleTime}
            open={isDateTimePickerOpen}
            onClose={() => setIsDateTimePickerOpen(false)}
            onAccept={handleDateChange}
            closeOnSelect={false}
            onChange={() => {}}
            minutesStep={1}
            slotProps={{
              layout: {
                sx: {
                  display: 'flex',
                  flexDirection: 'column',
                }
              }
            }}
            slots={{
              actionBar: ({ onAccept, onCancel }) => {
                const handleAcceptWithSaleType = () => {
                  params.saleType = localSaleType;
                  onAccept();
                };

                return (
                  <div style={{ padding: '8px 16px', display: 'flex', gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                    <FormControl>
                      <RadioGroup
                        row
                        value={localSaleType}
                        onChange={(e) => setLocalSaleType(Number(e.target.value))}
                        sx={{ justifyContent: 'center' }}
                      >
                        <FormControlLabel value={1} control={<Radio />} label="PreSale" />
                        <FormControlLabel value={2} control={<Radio />} label="OnSale" />
                      </RadioGroup>
                    </FormControl>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <Button onClick={onCancel}>Cancel</Button>
                      <Button onClick={handleAcceptWithSaleType} variant="contained">OK</Button>
                    </div>
                  </div>
                );
              }
            }}
          />
        </LocalizationProvider>
      ) : (
        <Select 
          value={params.value ? (generateSalesDatesValueOptions(params).find(opt => opt.value === params.value) ? params.value : '') : ''}
          onChange={handleSelectChange} 
          fullWidth 
          sx={{ width: "100%"}}
        >
          {generateSalesDatesValueOptions(params)?.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      )}
    </div>
  );
};

export default EditSaleDateCell;